import { useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import './TrophySpotlights.css';

// Decorative viewport lighting; buttons and live values stay in the scene.
export default function TrophySpotlights({ sceneRef }) {
  const [lights, setLights] = useState([]);
  useLayoutEffect(() => {
    const scene = sceneRef.current;
    if (!scene) return;
    const cards = [...scene.querySelectorAll('.vault-crystal-card')];
    const hovering = new Set(), focused = new Set();
    const hoverMedia = window.matchMedia('(any-hover: hover)');
    let frame;
    const measure = () => {
      setLights(cards.map(card => {
        const art = card.querySelector('.vault-crystal-art');
        const rect = art.getBoundingClientRect();
        const active = (hovering.has(card) || focused.has(card)) && rect.bottom > 0 && rect.top < window.innerHeight;
        card.dataset.spotlit = String(active);
        return { key: card.dataset.metric, active, x: rect.left + rect.width / 2,
          width: rect.width * 1.85, height: Math.max(0, rect.bottom) };
      }));
    };
    const schedule = () => { cancelAnimationFrame(frame); frame = requestAnimationFrame(measure); };
    const cleanups = cards.map(card => {
      const enter = e => { if (e.pointerType === 'touch' || !hoverMedia.matches) return; hovering.add(card); schedule(); };
      const leave = () => { hovering.delete(card); schedule(); };
      const focus = () => { if (card.matches(':focus-visible')) focused.add(card); schedule(); };
      const blur = () => { focused.delete(card); schedule(); };
      card.addEventListener('pointerenter', enter); card.addEventListener('pointerleave', leave);
      card.addEventListener('focus', focus); card.addEventListener('blur', blur);
      return () => {
        card.removeEventListener('pointerenter', enter); card.removeEventListener('pointerleave', leave);
        card.removeEventListener('focus', focus); card.removeEventListener('blur', blur);
        delete card.dataset.spotlit;
      };
    });
    const clear = () => { hovering.clear(); focused.clear(); schedule(); };
    const visibility = () => { if (document.hidden) clear(); };
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
    [scene, document.querySelector('.cockpit-header'), ...cards].filter(Boolean).forEach(el => observer?.observe(el));
    window.addEventListener('resize', schedule);
    window.addEventListener('scroll', schedule, true);
    window.addEventListener('blur', clear);
    document.addEventListener('visibilitychange', visibility);
    measure();
    return () => {
      cancelAnimationFrame(frame); observer?.disconnect(); cleanups.forEach(cleanup => cleanup());
      window.removeEventListener('resize', schedule); window.removeEventListener('scroll', schedule, true);
      window.removeEventListener('blur', clear); document.removeEventListener('visibilitychange', visibility);
    };
  }, [sceneRef]);
  return createPortal(<div className="trophy-spotlights" aria-hidden="true">
    {lights.map(light => <div key={light.key} className="trophy-spotlight" data-metric={light.key}
      data-active={light.active} style={{left: light.x, width: light.width, height: light.height}}>
      <div className="trophy-spotlight-cone"/><div className="trophy-spotlight-pool"/>
    </div>)}
  </div>, document.body);
}
